import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AdminWrapper from "../../../components/AdminWrapper";
import ImageZoomModal from "../../../components/ImageZoomModal";
import {
  GetOtsFeeReceipts,
  VerifyOtsFeeReceipt,
  RejectOtsFeeReceipt,
} from "../../../redux/reducer/OtsFeeReceipts";
import { userCan, PERMISSIONS } from "../../../config/adminRoles";
import { FollowUpDialog, FollowUpFilterBar, FollowUpStatus } from "../../../components/followUp/FollowUpUI";
import {
  matchesFilter,
  matchesSearch,
  rowSx,
  sortByUrgency,
  useFollowUpOverrides,
} from "../../../components/followUp/followUpUtils";

const TABS = [
  // Owner's cash payment is verified, fee is owed, tenant has sent nothing —
  // this is the team's call list, not a reviewer's queue.
  { key: "not_submitted", label: "Not Paid Yet — Call" },
  { key: "pending", label: "Awaiting Review" },
  { key: "verified", label: "Verified" },
  { key: "rejected", label: "Rejected" },
  { key: "cancelled", label: "Cancelled" },
];

const CHIP = {
  not_submitted: { bg: "#fee2e2", color: "#991b1b" },
  pending: { bg: "#fef3c7", color: "#92400e" },
  verified: { bg: "#dcfce7", color: "#166534" },
  rejected: { bg: "#fee2e2", color: "#991b1b" },
  cancelled: { bg: "#e5e7eb", color: "#374151" },
};

const STATUS_LABEL = {
  not_submitted: "not submitted",
  pending: "pending review",
};

// A receipt can only be verified/rejected once the tenant has actually sent
// one — `otsFee.status` alone can't tell the two apart (see the server-side
// comment in OtsFeeReviewController.js), so submittedAt is the real gate.
const canReview = (row) =>
  Boolean(row?.otsFee?.status === "pending" && row?.otsFee?.submittedAt);

// Only these two lists are about chasing someone: a fee nobody has paid yet,
// and a receipt that was rejected and is waiting on the tenant to resend.
const FOLLOW_UP_TABS = ["not_submitted", "rejected"];

const money = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
const when = (d) => (d ? moment(d).format("DD MMM YYYY, h:mm A") : "—");

// Cash bookings: the tenant pays the owner directly and sends OTS its service
// fee separately. The receipt for that fee is reviewed here; until it is
// approved the owner cannot confirm the move-in, so the tenancy cannot start.
const OtsFeeReceipts = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { loading, items, counts } = useSelector((state) => state.otsFeeReceipts);
  const currentUser = useSelector((state) => state.validateUser?.UserValidate?.data?.user);
  // Someone holding only VIEW_OTS_FEE_RECEIPTS can see this whole queue but
  // not act on it — verifying or rejecting still needs the full
  // VERIFY_PAYMENTS permission (see routes/adminRoutes.js).
  const canAct = userCan(currentUser, PERMISSIONS.VERIFY_PAYMENTS);

  const [tab, setTab] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [followRow, setFollowRow] = useState(null);
  const { record, apply } = useFollowUpOverrides();
  const [zoom, setZoom] = useState({ open: false, images: [], key: 0 });

  const load = useCallback(
    (status) => dispatch(GetOtsFeeReceipts({ status })),
    [dispatch]
  );

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const tracking = FOLLOW_UP_TABS.includes(tab);
  const trackedItems = tracking ? items.map(apply) : items;
  const visibleItems = tracking
    ? sortByUrgency(trackedItems.filter((r) => matchesFilter(r, filter) && matchesSearch(r, search)))
    : items;

  const closeDialog = () => {
    if (busy) return;
    setSelected(null);
    setRejecting(false);
    setReason("");
  };

  const openZoom = (images, startIndex) =>
    setZoom({
      open: true,
      images: images.filter(Boolean),
      // ImageZoomModal reads its start index once, so a new key remounts it.
      key: Date.now() + startIndex,
    });

  const handleApprove = async () => {
    const result = await Swal.fire({
      title: "Verify this receipt?",
      text: `Confirm that ${money(selected.otsFee.amount)} was received in the OTS account. The owner will then be able to confirm the move-in.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, verify",
    });
    if (!result.isConfirmed) return;

    setBusy(true);
    const res = await dispatch(VerifyOtsFeeReceipt(selected.requestId));
    setBusy(false);

    if (res?.meta?.requestStatus === "fulfilled") {
      toast.success("Receipt verified");
      setSelected(null);
      load(tab);
    } else {
      toast.error(res?.payload?.message || "Failed to verify the receipt");
    }
  };

  const handleReject = async () => {
    if (reason.trim().length < 3) {
      toast.error("Please give the tenant a reason");
      return;
    }

    setBusy(true);
    const res = await dispatch(
      RejectOtsFeeReceipt({ requestId: selected.requestId, reason: reason.trim() })
    );
    setBusy(false);

    if (res?.meta?.requestStatus === "fulfilled") {
      toast.success("Receipt rejected. The tenant has been told.");
      setSelected(null);
      setRejecting(false);
      setReason("");
      load(tab);
    } else {
      toast.error(res?.payload?.message || "Failed to reject the receipt");
    }
  };

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          OTS Fee Receipts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cash bookings: the tenant pays the owner directly and sends OTS its
          service fee separately. Verify that the fee reached the OTS account —
          the tenancy cannot start until you do.
        </Typography>

        <Paper sx={{ mb: 2, borderRadius: 2 }}>
          <Tabs
            value={tab}
            onChange={(e, value) => setTab(value)}
            sx={{
              borderBottom: "1px solid #e5e7eb",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
            }}
          >
            {TABS.map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                label={`${t.label} (${counts?.[t.key] ?? 0})`}
              />
            ))}
          </Tabs>
        </Paper>

        {tracking && (
          <FollowUpFilterBar
            rows={trackedItems}
            search={search}
            onSearch={setSearch}
            filter={filter}
            onFilter={setFilter}
          />
        )}

        <Paper sx={{ borderRadius: 2, overflowX: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : visibleItems.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography color="text.secondary">
                {items.length > 0 ? "No rows match this filter. " : ""}No {TABS.find((t) => t.key === tab)?.label.toLowerCase()} receipts.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell align="right">OTS fee</TableCell>
                  <TableCell>{tab === "not_submitted" ? "Waiting since" : "Submitted"}</TableCell>
                  <TableCell>Status</TableCell>
                  {tracking && <TableCell>Follow-up</TableCell>}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleItems.map((row) => {
                  const notSubmitted = row.daysWaiting != null;
                  const rowState = notSubmitted ? "not_submitted" : row.otsFee.status;
                  return (
                    <TableRow key={row.requestId} hover sx={tracking ? rowSx(row) : undefined}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.tenantName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.tenantEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.tenantPhone || "—"}</TableCell>
                      <TableCell>
                        {row.flatName}
                        {row.flatCity ? `, ${row.flatCity}` : ""}
                      </TableCell>
                      <TableCell>{row.landlordName || "—"}</TableCell>
                      <TableCell align="right">
                        <strong>{money(row.otsFee.amount)}</strong>
                      </TableCell>
                      <TableCell>
                        {notSubmitted
                          ? `${row.daysWaiting} day${row.daysWaiting === 1 ? "" : "s"}`
                          : when(row.otsFee.submittedAt)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABEL[rowState] || rowState}
                          sx={{
                            bgcolor: CHIP[rowState]?.bg,
                            color: CHIP[rowState]?.color,
                            textTransform: "capitalize",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      {tracking && (
                        <TableCell>
                          <FollowUpStatus row={row} onOpen={() => setFollowRow(row)} />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Button size="small" onClick={() => setSelected(row)}>
                          {!canAct
                            ? "View"
                            : notSubmitted
                            ? "View"
                            : row.otsFee.status === "pending"
                            ? "Review"
                            : "View"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <Dialog open={Boolean(selected)} onClose={closeDialog} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              OTS fee receipt — {selected.tenantName}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Property
                  </Typography>
                  <Typography variant="body2">
                    {selected.flatName}
                    {selected.flatCity ? `, ${selected.flatCity}` : ""}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Owner
                  </Typography>
                  <Typography variant="body2">{selected.landlordName || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    OTS fee to verify
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {money(selected.otsFee.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Paid to the owner (cash)
                  </Typography>
                  <Typography variant="body2">{money(selected.otsFee.ownerAmount)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Move-in date
                  </Typography>
                  <Typography variant="body2">{selected.moveInDate || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Contact
                  </Typography>
                  <Typography variant="body2">{selected.tenantPhone || selected.tenantEmail}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Owner's verification of their cash
                  </Typography>
                  <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                    {selected.paymentStatus}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    OTS fee receipt (to verify)
                  </Typography>
                  {selected.otsFee.receipt ? (
                    <Box
                      component="img"
                      src={selected.otsFee.receipt}
                      alt="OTS fee receipt"
                      onClick={() =>
                        openZoom([selected.otsFee.receipt, selected.ownerReceipt], 0)
                      }
                      sx={{
                        width: "100%",
                        maxHeight: 320,
                        objectFit: "contain",
                        border: "1px solid #e5e7eb",
                        borderRadius: 1,
                        cursor: "zoom-in",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No receipt image.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tenant's receipt for the owner (for comparison)
                  </Typography>
                  {selected.ownerReceipt ? (
                    <Box
                      component="img"
                      src={selected.ownerReceipt}
                      alt="Owner payment receipt"
                      onClick={() =>
                        openZoom([selected.ownerReceipt, selected.otsFee.receipt], 0)
                      }
                      sx={{
                        width: "100%",
                        maxHeight: 320,
                        objectFit: "contain",
                        border: "1px solid #e5e7eb",
                        borderRadius: 1,
                        cursor: "zoom-in",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No receipt image.
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {selected.otsFee.status === "rejected" && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Rejected: {selected.otsFee.rejectionReason || "no reason recorded"}. The
                  tenant has until {when(selected.otsFee.resubmitDeadline)} to send a correct
                  receipt.
                </Alert>
              )}

              {selected.otsFee.status === "pending" && !selected.otsFee.submittedAt && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Nothing to review yet — the tenant hasn't sent a receipt. Call{" "}
                  {selected.tenantPhone || selected.tenantEmail} to remind them to pay the OTS
                  service fee and upload the receipt from My Requests.
                </Alert>
              )}

              {!canAct && canReview(selected) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  View-only access — verifying or rejecting this receipt needs full payments
                  permission.
                </Alert>
              )}

              {canAct && canReview(selected) && rejecting && (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Reason for rejecting (the tenant will see this)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  sx={{ mt: 2 }}
                  autoFocus
                />
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={closeDialog} disabled={busy}>
                Close
              </Button>
              {canAct &&
                canReview(selected) &&
                (rejecting ? (
                  <>
                    <Button onClick={() => setRejecting(false)} disabled={busy}>
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleReject}
                      disabled={busy}
                      startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {busy ? "Rejecting…" : "Reject receipt"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button color="error" onClick={() => setRejecting(true)} disabled={busy}>
                      Reject
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleApprove}
                      disabled={busy}
                      startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {busy ? "Verifying…" : "Verify"}
                    </Button>
                  </>
                ))}
            </DialogActions>
          </>
        )}
      </Dialog>

      <FollowUpDialog
        open={Boolean(followRow)}
        onClose={() => setFollowRow(null)}
        title={followRow?.tenantName || "Follow-up"}
        subtitle="OTS service fee"
        details={
          followRow
            ? [
                { label: "Property", value: [followRow.flatName, followRow.flatCity].filter(Boolean).join(", ") },
                { label: "OTS fee", value: money(followRow.otsFee.amount) },
                { label: "Paid to owner (cash)", value: money(followRow.otsFee.ownerAmount) },
                { label: "Move-in date", value: followRow.moveInDate },
                { label: "Owner", value: followRow.landlordName },
                {
                  label: followRow.daysWaiting != null ? "Fee owed for" : "Rejected",
                  value:
                    followRow.daysWaiting != null
                      ? `${followRow.daysWaiting} day${followRow.daysWaiting === 1 ? "" : "s"}`
                      : followRow.otsFee.rejectionReason || "",
                },
              ]
            : []
        }
        contacts={
          followRow
            ? [{ party: "tenant", name: followRow.tenantName, phone: followRow.tenantPhone, email: followRow.tenantEmail }]
            : []
        }
        followUp={followRow ? apply(followRow).followUp : null}
        onSaved={record}
      />

      <ToastContainer position="top-right" />

      <ImageZoomModal
        key={zoom.key}
        open={zoom.open}
        onClose={() => setZoom((z) => ({ ...z, open: false }))}
        images={zoom.images}
      />
    </>
  );
};

export default OtsFeeReceipts;
